import { useState, useEffect } from 'react';
import { adminApi } from '../../api/admin.js';
import { organizationsApi } from '../../api/organizations.js';
import { Modal } from '../../components/common/Modal.jsx';
import { FormField, Input, Select } from '../../components/common/FormField.jsx';
import { LoadingSpinner } from '../../components/common/LoadingSpinner.jsx';
import { ErrorAlert } from '../../components/common/ErrorAlert.jsx';
import { usePageHeader } from '../../hooks/usePageHeader.jsx';
import { formatDate } from '../../utils/dates.js';

export function UserAdminPage() {
  usePageHeader('User administration');
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inviteModal, setInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitingAdmin, setInvitingAdmin] = useState(false);
  const [inviteOrgId, setInviteOrgId] = useState('');
  const [saving, setSaving] = useState(null);

  const load = async () => {
    try {
      const [usersResult, orgsResult] = await Promise.all([
        adminApi.listUsers(),
        organizationsApi.list(),
      ]);
      setUsers(usersResult.users ?? []);
      setOrgs(orgsResult ?? []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setSaving('invite');
    try {
      await adminApi.inviteUser(inviteEmail, invitingAdmin, inviteOrgId || undefined);
      setInviteModal(false);
      setInviteEmail('');
      setInvitingAdmin(false);
      setInviteOrgId('');
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  };

  const toggleAdmin = async (user) => {
    const hasAdmin = (user.app_metadata?.roles ?? []).includes('admin');
    setSaving(user.id);
    try {
      await adminApi.updateUser(user.id, {
        roles: hasAdmin ? [] : ['admin'],
      });
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  };

  const updateOrg = async (user, newOrgId) => {
    setSaving(user.id + '-org');
    try {
      await adminApi.updateUser(user.id, { orgId: newOrgId || undefined });
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  };

  const deactivate = async (user) => {
    if (!confirm(`Deactivate ${user.email}? They will no longer be able to sign in.`)) return;
    setSaving(user.id + '-del');
    try {
      await adminApi.deleteUser(user.id);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <LoadingSpinner label="Loading users…" />;

  return (
    <div className="page-container" style={{ maxWidth: 'none' }}>
      <div className="between" style={{ marginBottom: 'var(--space-6)', justifyContent: 'flex-end' }}>
        <button className="btn btn-primary" onClick={() => setInviteModal(true)}>Invite user</button>
      </div>

      <ErrorAlert error={error} onDismiss={() => setError(null)} />

      {users.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)' }}>No users found.</p>
      ) : (
        <div className="data-table" style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Organization</th>
                <th>Confirmed</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const isAdmin = (user.app_metadata?.roles ?? []).includes('admin');
                const confirmed = Boolean(user.confirmed_at);
                const currentOrgId = user.app_metadata?.orgId ?? '';
                return (
                  <tr key={user.id}>
                    <td style={{ fontSize: 'var(--text-sm)' }}>{user.email}</td>
                    <td style={{ fontSize: 'var(--text-sm)' }}>
                      {user.user_metadata?.full_name ?? '—'}
                    </td>
                    <td>
                      <span className={`badge badge-${isAdmin ? 'primary' : 'neutral'}`}>
                        {isAdmin ? 'Admin' : 'Member'}
                      </span>
                    </td>
                    <td>
                      <select
                        className="form-select"
                        style={{ fontSize: 'var(--text-sm)', padding: '2px 6px' }}
                        value={currentOrgId}
                        disabled={saving === user.id + '-org'}
                        onChange={(e) => updateOrg(user, e.target.value)}
                      >
                        <option value="">—</option>
                        {orgs.map((org) => (
                          <option key={org.id} value={org.id}>{org.name}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <span className={`badge badge-${confirmed ? 'success' : 'warning'}`}>
                        {confirmed ? 'Yes' : 'Pending'}
                      </span>
                    </td>
                    <td style={{ fontSize: 'var(--text-sm)' }}>
                      {user.created_at ? formatDate(user.created_at) : '—'}
                    </td>
                    <td>
                      <div className="inline">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => toggleAdmin(user)}
                          disabled={saving === user.id}
                        >
                          {isAdmin ? 'Remove admin' : 'Make admin'}
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => deactivate(user)}
                          disabled={saving === user.id + '-del'}
                        >
                          Deactivate
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={inviteModal}
        onClose={() => setInviteModal(false)}
        title="Invite user"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setInviteModal(false)} disabled={saving === 'invite'}>Cancel</button>
            <button className="btn btn-primary" form="invite-form" type="submit" disabled={saving === 'invite'}>
              {saving === 'invite' ? 'Sending…' : 'Send invite'}
            </button>
          </>
        }
      >
        <form id="invite-form" onSubmit={invite} className="stack">
          <FormField label="Email address" required id="inviteEmail">
            <Input
              id="inviteEmail"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="member@chapter.org"
              required
            />
          </FormField>
          {orgs.length > 0 && (
            <FormField label="Organization" id="inviteOrg">
              <Select
                id="inviteOrg"
                value={inviteOrgId}
                onChange={(e) => setInviteOrgId(e.target.value)}
              >
                <option value="">— None —</option>
                {orgs.map((org) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </Select>
            </FormField>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={invitingAdmin}
              onChange={(e) => setInvitingAdmin(e.target.checked)}
            />
            Grant admin role
          </label>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            An invitation email will be sent. The user must confirm before they can sign in.
          </p>
        </form>
      </Modal>
    </div>
  );
}
